import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowRightCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

/**
 * Confirmation dialog shown before navigating away from the PQR Preview
 * to a source page in WPS Studio.
 *
 * Two variants share one shell:
 *   - `dirty=false` (clean): two buttons (Cancel / Continue) plus a
 *     "Don't ask again this session" checkbox. The caller is told
 *     whether the user ticked the checkbox via the `onContinue` arg.
 *   - `dirty=true`: three buttons (Cancel / Discard & Continue /
 *     Save & Continue). No suppression checkbox — staged edits MUST
 *     prompt every time, suppression cannot silently lose work.
 */

export interface PqrNavigateConfirmContinueArgs {
  /** True when the user ticked "Don't ask again this session". */
  suppressFuture: boolean
}

interface Props {
  open: boolean
  /** Human label for the clicked field, e.g. "IWM Operator". */
  fieldLabel: string
  /** Human label for the destination page, e.g. "Floor Sheet". */
  destinationLabel: string
  /** Scoped weld id — surfaced for context. `null` hides the weld line. */
  weldId: string | null
  /**
   * `true` when the PQR Preview staging buffer has unsaved edits.
   * Switches to the three-button dirty variant.
   */
  dirty?: boolean
  onCancel: () => void
  /** Clean-variant primary action. Receives the suppression checkbox state. */
  onContinue: (args: PqrNavigateConfirmContinueArgs) => void
  /** Dirty-variant: discard staged edits and proceed. */
  onDiscardAndContinue?: () => void
  /**
   * Dirty-variant: save staged edits and proceed if save succeeded.
   * Resolves `true` to trigger navigation, `false` to keep the user here
   * (e.g. save returned 409 / network error).
   */
  onSaveAndContinue?: () => Promise<boolean>
}

export default function PqrNavigateConfirmModal({
  open,
  fieldLabel,
  destinationLabel,
  weldId,
  dirty = false,
  onCancel,
  onContinue,
  onDiscardAndContinue,
  onSaveAndContinue
}: Props) {
  const [suppressFuture, setSuppressFuture] = useState(false)
  const [savingAndContinuing, setSavingAndContinuing] = useState(false)

  if (!open) return null

  const handleSaveAndContinue = async () => {
    if (!onSaveAndContinue) return
    setSavingAndContinuing(true)
    try {
      await onSaveAndContinue()
    } finally {
      setSavingAndContinuing(false)
    }
  }

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pqr-nav-confirm-title"
    >
      <div className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2
            id="pqr-nav-confirm-title"
            className="text-lg font-semibold text-gray-900"
          >
            Leave PQR Preview to edit this field?
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={onCancel}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-700">
          <p>
            You're about to leave the PQR Preview to edit{' '}
            <strong className="text-gray-900">{fieldLabel}</strong> on the{' '}
            <strong className="text-gray-900">{destinationLabel}</strong>
            {weldId ? (
              <>
                {' '}for <strong className="text-gray-900">Weld {weldId}</strong>
              </>
            ) : null}
            .
          </p>
          <p className="text-xs text-gray-500">
            You'll be brought back here when you click Back on the next page.
          </p>
          {dirty ? (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-hero-red-30 bg-hero-red-10 px-3 py-2 text-xs text-hero-red-90">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                You have unsaved PQR header changes — choose how to handle them.
              </span>
            </div>
          ) : null}
        </div>

        {!dirty ? (
          <label className="mt-4 flex items-center gap-2 text-xs text-heritage-grey">
            <input
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
              checked={suppressFuture}
              onChange={(e) => setSuppressFuture(e.target.checked)}
            />
            <span>Don't ask again this session</span>
          </label>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={savingAndContinuing}
          >
            Cancel
          </button>
          {dirty ? (
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => onDiscardAndContinue?.()}
                disabled={savingAndContinuing}
              >
                Discard & Continue
              </button>
              <button
                type="button"
                className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleSaveAndContinue}
                disabled={savingAndContinuing}
              >
                <ArrowRightCircleIcon className="h-4 w-4" />
                {savingAndContinuing ? 'Saving…' : 'Save & Continue'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => onContinue({ suppressFuture })}
            >
              <ArrowRightCircleIcon className="h-4 w-4" />
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
